import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import { notifyError, notifySuccess } from '../../../utils/notify';
import Dialog from '../../../components/Dialog';
import ConfirmAction from '../../../components/dialogs/ConfirmAction';
import useForm from '../../../utils/useForm';
import { getCurrentNoticeId, closeEditNoticeDialog } from '../store';
import {
  apiAdminOneNoticeBoard,
  apiAdminCreateNoticeBoard,
  apiAdminUpdateNoticeBoard,
  apiAdminDeleteNoticeBoard,
} from '../../../common/api/admin';
import { default as NoticeBoardSelect } from '../../../common/selectors/NoticeBoard';

const EditNoticeDialog = ({
  open,
  onCancel,
  canDelete,
  canUpdate,
  canCreate,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentNoticeId = useSelector(getCurrentNoticeId);
  const [isConfirmLoading, setConfirmLoading] = useState(false);
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);

  const { ready: isReady } = useDDPSubscription(
    currentNoticeId &&
      apiAdminOneNoticeBoard.withParams({
        noticeId: currentNoticeId,
      }),
  );

  const notice = useSelector(
    currentNoticeId
      ? NoticeBoardSelect.one().whereIdEquals(currentNoticeId)
      : () => null,
  );

  const typeOptions = [
    {
      label: 'Info',
      value: 'info',
    },
    {
      label: 'Warning',
      value: 'warning',
    },
    {
      label: 'Success',
      value: 'success',
    },
  ];

  const { handleSubmit, handleResetForm, renderField } = useForm({
    inputs: {
      type: {
        testId: 'form-field-type',
        label: t('type'),
        type: 'react-select',
        defaultValue: notice?.type,
        options: typeOptions,
        schema: {
          type: String,
        },
      },
      subject: {
        testId: 'form-field-subject',
        label: t('subject'),
        type: 'text',
        // TODO: Replace group with notice
        defaultValue: notice?.subject,
        isMultiLanguage: true,
        schema: {
          type: String,
        },
      },
      content: {
        testId: 'form-field-content',
        label: t('content'),
        type: 'markdown',
        // TODO: Replace group with notice
        defaultValue: notice?.content,
        isMultiLanguage: true,
        schema: {
          type: String,
        },
      },
      author: {
        testId: 'form-field-author',
        label: t('author'),
        type: 'text',
        defaultValue: notice?.author,
        schema: {
          type: String,
        },
      },
      dateStart: {
        testId: 'form-field-date-start',
        label: t('forms:dateStart.label'),
        type: 'date',
        defaultValue: notice?.dateStart,
        schema: {
          type: String,
        },
      },
      dateEnd: {
        testId: 'form-field-date-end',
        label: t('forms:dateEnd.label'),
        type: 'date',
        defaultValue: notice?.dateEnd,
        schema: {
          type: String,
        },
      },
      timeStart: {
        testId: 'form-field-time-start',
        label: t('forms:timeStart.label'),
        type: 'time',
        defaultValue: notice?.timeStart,
        schema: {
          type: String,
        },
      },
      timeEnd: {
        testId: 'form-field-time-end',
        label: t('forms:timeEnd.label'),
        type: 'time',
        defaultValue: notice?.timeEnd,
        schema: {
          type: String,
        },
      },
      timezone: {
        testId: 'form-field-timezone',
        label: t('forms:timezone.label'),
        placeholder: t('forms:timezone.label'),
        type: 'search',
        valueSetId: 'zedoc/timezones@1.0.0',
        defaultValue: notice?.timezone,
        schema: {
          type: String,
        },
      },
    },
    onSubmit: useCallback(
      (values) => {
        setConfirmLoading(true);

        const apiSpec = currentNoticeId
          ? apiAdminUpdateNoticeBoard
          : apiAdminCreateNoticeBoard;
        const data = currentNoticeId
          ? { noticeId: currentNoticeId, ...values }
          : values;

        dispatch(callMethod(apiSpec, data))
          .then(() => {
            notifySuccess(
              currentNoticeId
                ? t('confirmations:updateNotice.success')
                : t('confirmations:createNotice.success'),
            );
            dispatch(closeEditNoticeDialog());
            handleResetForm();
          })
          .catch((error) => {
            notifyError()(error);
          })
          .finally(() => {
            setConfirmLoading(false);
          });
      },
      [currentNoticeId, dispatch, t, handleResetForm],
    ),
  });

  const handleOnCancel = () => {
    onCancel();
    handleResetForm();
  };

  const handleOpenDeleteDialog = useCallback(() => {
    setIsDeleteDialogVisible(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogVisible(false);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!currentNoticeId) return;

    setConfirmLoading(true);

    dispatch(
      callMethod(apiAdminDeleteNoticeBoard, { noticeId: currentNoticeId }),
    )
      .then(() => {
        notifySuccess(t('confirmations:deleteNotice.success'));
        dispatch(closeEditNoticeDialog());
      })
      .catch(notifyError())
      .finally(() => {
        setConfirmLoading(false);
        setIsDeleteDialogVisible(false);
      });
  }, [currentNoticeId, dispatch, t]);

  return (
    <>
      <Dialog
        title={currentNoticeId ? t('editNotice') : t('addNotice')}
        size="large"
        visible={open}
        loading={isConfirmLoading}
        isOkDisabled={
          currentNoticeId ? (!canUpdate ? true : !isReady) : !canCreate
        }
        okText={currentNoticeId ? t('save') : t('add')}
        onOk={handleSubmit}
        onCancel={handleOnCancel}
        extraActions={
          currentNoticeId &&
          canDelete && [
            {
              key: 'delete',
              text: t('delete'),
              type: 'primary',
              onClick: handleOpenDeleteDialog,
              disabled: isConfirmLoading,
            },
          ]
        }
      >
        <form onSubmit={handleSubmit} className="stack-6">
          {renderField('type')}
          {renderField('subject')}
          {renderField('content')}
          {renderField('author')}
          {renderField('timezone')}
          <div className="grid grid-cols-2 gap-4">
            {renderField('dateStart')}
            {renderField('timeStart')}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {renderField('dateEnd')}
            {renderField('timeEnd')}
          </div>
        </form>
      </Dialog>
      <ConfirmAction
        open={isDeleteDialogVisible}
        title={t('confirmations:deleteNotice.title')}
        message={t('confirmations:deleteNotice.message')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteDialog}
        loading={isConfirmLoading}
      />
    </>
  );
};

EditNoticeDialog.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  canDelete: PropTypes.bool,
  canUpdate: PropTypes.bool,
};

EditNoticeDialog.defaultProps = {
  open: false,
};

export default EditNoticeDialog;
